// src/lib/sentry.ts
// Sentry error monitoring configuration for DayOne
import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * Initialize Sentry error monitoring.
 * Only initializes if VITE_SENTRY_DSN is set.
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.log("[Sentry] Skipped: VITE_SENTRY_DSN not set");
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE || "development",

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay for debugging (only in production, 10% of sessions)
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on errors

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /^ResizeObserver loop/,
      /^Non-Error promise rejection/,
      // Network errors users can't fix
      /^Failed to fetch/,
      /^NetworkError/,
      /^Load failed/,
      // Safari-specific
      /^undefined is not an object/,
      // Monaco editor noise
      /^Cannot read properties of null \(reading 'model'\)/,
    ],

    // Don't send errors from sandbox/preview iframes
    denyUrls: [
      /\/__render/,
      /\/__preview/,
      /\/__sandbox/,
      /esm\.sh/,
    ],

    // Add context to all events
    beforeSend(event, hint) {
      // Add user context if available
      const user = localStorage.getItem("sb-cwqeyefgtnsgddajtdfp-auth-token");
      if (user) {
        try {
          const parsed = JSON.parse(user);
          if (parsed?.user?.id) {
            event.user = {
              id: parsed.user.id,
              email: parsed.user.email,
            };
          }
        } catch {
          // Ignore parse errors
        }
      }

      // Tag edge function errors
      const error = hint.originalException;
      if (error instanceof Error) {
        if (error.message.includes("edge function") || error.message.includes("supabase.co/functions")) {
          event.tags = { ...event.tags, errorType: "edge_function" };
        }
        if (error.message.includes("builder-chat") || error.message.includes("stream-app-build")) {
          event.tags = { ...event.tags, errorType: "builder" };
        }
        if (error.message.includes("deploy-to-vercel")) {
          event.tags = { ...event.tags, errorType: "deploy" };
        }
      }

      return event;
    },

    integrations: [
      // Capture console.error calls
      Sentry.captureConsoleIntegration({ levels: ["error"] }),

      // Track browser navigation
      Sentry.browserTracingIntegration(),

      // Session replay for debugging production issues
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });

  console.log("[Sentry] Initialized");
}

/**
 * Capture a custom error with additional context.
 */
export function captureError(
  error: Error | string,
  context?: Record<string, unknown>
): void {
  if (!SENTRY_DSN) return;

  const err = typeof error === "string" ? new Error(error) : error;

  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context);
      Sentry.captureException(err);
    });
  } else {
    Sentry.captureException(err);
  }
}

/**
 * Capture a failed API call to edge functions.
 */
export function captureEdgeFunctionError(
  functionName: string,
  status: number,
  errorMessage: string,
  requestBody?: unknown
): void {
  if (!SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    scope.setTag("errorType", "edge_function");
    scope.setTag("functionName", functionName);
    scope.setExtra("status", status);
    scope.setExtra("requestBody", requestBody);
    Sentry.captureMessage(`Edge function error: ${functionName} (${status}) - ${errorMessage}`, "error");
  });
}

/**
 * Set user context for all subsequent errors.
 */
export function setUser(userId: string, email?: string): void {
  if (!SENTRY_DSN) return;
  Sentry.setUser({ id: userId, email });
}

/**
 * Clear user context on logout.
 */
export function clearUser(): void {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  if (!SENTRY_DSN) return;
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: "info",
  });
}

// Re-export Sentry's ErrorBoundary for convenience
export { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
